/**
 * Emerge Privacy Consent — vanilla TS IIFE.
 *
 * Espone window.EmergePrivacy con API per gestire lo stato del consenso.
 * Non renderizza UI, salvo data-banner="true": allora monta il banner integrato
 * (banner.ts) per i siti senza un proprio componente, come i siti statici fatti da terzi.
 *
 * Configurazione tramite attributi data-* sullo <script>:
 *   <script src=".../privacy-consent.js"
 *           data-tid="55d6b049-d6d4-4a7b-a8ff-0a3415ba56d2" defer></script>
 *
 * data-tid è policy_sites.id (uuid), che finisce nel path degli endpoint
 * /api/policy/{siteId}/... Non è un codice "PRIVACY-2026-...": quei codici
 * appartenevano a policy_activations, tabella mai creata, e con un valore
 * non-uuid la registrazione del consenso falla in silenzio.
 *
 * Oppure variabile globale prima del caricamento:
 *   window.EMERGE_PRIVACY_TID = '55d6b049-d6d4-4a7b-a8ff-0a3415ba56d2';
 */

import type {
  ClearLocalDataResult,
  ConsentChangeListener,
  ConsentState,
  EmergePrivacyAPI,
  UpdateConsentInput,
} from './types';
import { buildState, readState, storageKey, writeState } from './storage';
import { configureApiBase, deleteConsentServer, getApiBase, postConsent } from './sync';
import { fireGtmConsentDefault, fireGtmConsentUpdate } from './gtm-bridge';
import { avviaBanner, bannerRichiesto, caricaConfigBannerRemota, mostraErrorePubblicazione, unisciConfigBanner } from './banner';
import { clearVisitorUid, peekVisitorUid } from './visitor-uid';
import { createReleaseConsent, hasOptionalConsent } from './release-consent';
import { blockUnsupportedNativeRelease } from './unsupported-native';

declare global {
  interface Window {
    EMERGE_PRIVACY_TID?: string;
    EMERGE_PRIVACY_API_BASE?: string;
  }
}

(function init() {
  if (typeof window === 'undefined') return;

  // Una CMP del sito può usare lo stesso globale. Solo il marcatore del widget
  // identifica un caricamento duplicato; i metodi della CMP restano prioritari.
  const globalePreesistente = window.EmergePrivacy as
    | (Record<string, unknown> & { __emergeWidget?: true })
    | undefined;
  if (globalePreesistente?.__emergeWidget) {
    // già inizializzato da un altro tag <script> di questo widget
    return;
  }

  // Recupero tracking_id
  const currentScript = document.currentScript as HTMLScriptElement | null;
  const tid =
    window.EMERGE_PRIVACY_TID ??
    currentScript?.getAttribute('data-tid') ??
    null;

  // API base (override per staging/dev)
  const apiBase =
    window.EMERGE_PRIVACY_API_BASE ?? currentScript?.getAttribute('data-api-base');
  if (apiBase) configureApiBase(apiBase);

  // Un sito che fornisce già la propria API ha una CMP propria: non leggiamo
  // né ripuliamo il suo storage, così il widget resta strettamente observe-only.
  const consensoGestitoDalSito = globalePreesistente !== undefined;
  const releaseMode = currentScript?.getAttribute('data-consent-mode') === 'release';
  if (consensoGestitoDalSito && releaseMode) {
    blockUnsupportedNativeRelease(globalePreesistente, tid);
    return;
  }
  const saved = consensoGestitoDalSito ? null : readState(tid);
  let state: ConsentState | null = releaseMode && saved && hasOptionalConsent(saved.categories) ? null : saved;
  const listeners = new Set<ConsentChangeListener>();

  function notifyAll(next: ConsentState) {
    listeners.forEach((cb) => {
      try {
        cb(next);
      } catch (err) {
        // un listener rotto non deve bloccare gli altri
        console.warn('[EmergePrivacy] listener error', err);
      }
    });
  }

  function applyState(next: ConsentState) {
    const revoked = Object.keys(state?.categories ?? {}).filter(
      (category) => category !== 'necessary' && state?.categories[category] === true && next.categories[category] !== true,
    );
    state = next;
    writeState(tid, next);
    fireGtmConsentUpdate(next.categories);
    notifyAll(next);
    if (revoked.length) window.dispatchEvent(new CustomEvent('emerge-privacy-revoke', { detail: { categories: revoked } }));
  }

  if (releaseMode) fireGtmConsentDefault();
  const lang = (currentScript?.getAttribute('data-lang') ?? document.documentElement?.lang ?? 'it').slice(0, 2).toLowerCase() || 'it';
  const releaseConsent = releaseMode ? createReleaseConsent({
    tid, apiBase: getApiBase(), lang, saved, apply: applyState,
    releaseId: currentScript?.getAttribute('data-release-id') ?? undefined,
  }) : null;

  const api: EmergePrivacyAPI = {
    trackingId: tid,
    consentMode: releaseMode ? 'release' : 'legacy',
    getConsent: () => state,
    updateConsent: async (input: UpdateConsentInput) => {
      if (releaseConsent) return releaseConsent.update(input);
      const next = buildState(input.categories);
      const source = input.source ?? 'programmatic';
      applyState(next);
      await postConsent(tid, next, source);
    },
    onConsentChange: (cb: ConsentChangeListener) => {
      listeners.add(cb);
      // se abbiamo già uno stato, fire immediatamente con esso
      if (state) {
        try {
          cb(state);
        } catch (err) {
          console.warn('[EmergePrivacy] listener error', err);
        }
      }
      return () => listeners.delete(cb);
    },
    openPreferences: () => {
      // emette evento custom — UI banner la gestisce il cliente
      window.dispatchEvent(new CustomEvent('emerge-privacy-open-preferences'));
    },
    clearLocalData: async (): Promise<ClearLocalDataResult> => {
      releaseConsent?.invalidate();
      // La cancellazione remota può attendere la rete; la revoca locale no.
      applyState(buildState({ necessary: true }));
      state = null;
      const visitorUid = peekVisitorUid();

      // Pulizia prima dell'await: una scelta nuova durante la DELETE non viene
      // cancellata dalla risposta tardiva della richiesta precedente.
      let clearedLocal = false;
      try {
        localStorage.removeItem(storageKey(tid));
        clearVisitorUid();
        clearedLocal = true;
      } catch {
        clearedLocal = false;
      }

      // Cancella server-side l'identificatore precedente, se presente.
      let serverResult: Awaited<ReturnType<typeof deleteConsentServer>> = {
        ok: false,
        error: 'no_visitor_uid',
      };
      if (visitorUid && tid) {
        serverResult = await deleteConsentServer(tid, visitorUid);
      } else if (!tid) {
        serverResult = { ok: false, error: 'no_tid' };
      } else {
        // Niente visitor_uid → niente consent record server-side da cancellare:
        // consideriamo l'operazione server "ok" (idempotente).
        serverResult = { ok: true };
      }

      // Evento per la UI, senza sovrascrivere eventuali scelte più recenti.
      try {
        window.dispatchEvent(
          new CustomEvent('emerge-privacy-data-cleared', {
            detail: {
              cleared_local: clearedLocal,
              cleared_server: serverResult.ok,
            },
          }),
        );
      } catch {
        /* ignore */
      }

      return {
        cleared_local: clearedLocal,
        cleared_server: serverResult.ok,
        consent_records_deleted: serverResult.consent_records_deleted,
        observations_deleted: serverResult.observations_deleted,
        error: serverResult.error,
      };
    },
  };

  // Con una CMP preesistente non scriviamo nel suo storage né emettiamo
  // segnali GTM: scadenza, registrazione e revoca restano sotto il suo controllo.
  const apiEsposta: Partial<EmergePrivacyAPI> = consensoGestitoDalSito
    ? {
        trackingId: api.trackingId,
        getConsent: api.getConsent,
        onConsentChange: api.onConsentChange,
      }
    : api;

  // I metodi del sito vincono, in particolare openPreferences: ciascuna CMP
  // collega questa API alla propria modale e al proprio evento di apertura.
  window.EmergePrivacy = Object.assign({}, apiEsposta, globalePreesistente, {
    __emergeWidget: true as const,
  });

  // Se c'era già uno stato salvato, fire GTM bridge subito (per tag manager
  // che si caricano dopo il widget). Salvo dove il consenso lo gestisce il
  // sito: vedi sopra.
  if (state && !consensoGestitoDalSito) {
    fireGtmConsentUpdate(state.categories);
  }

  // data-audit-mode è ignorato anche sui tag già installati. L'inventario
  // viene raccolto dai controlli sintetici, mai dal browser dei visitatori.

  // Banner integrato: solo dove il consenso lo gestiamo noi (non sui siti con
  // una propria CMP) e solo se richiesto dal tag. Il banner compare quando non
  // c'e' una scelta valida e ci sono categorie da consentire.
  if (!consensoGestitoDalSito && bannerRichiesto(currentScript)) {
    if (releaseConsent) {
      void releaseConsent.restored.catch(() => releaseConsent.ready).then(({ publication }) => {
        const config = publication.banner;
        // Nessun override del tag può alterare le categorie/documenti della prova.
        avviaBanner(api, {
          categories: config.categories, lang: publication.lang,
          privacyUrl: config.privacy_url, cookieUrl: config.cookie_url, accent: config.accent,
          enabled: config.enabled,
          publication,
        });
      }).catch(() => mostraErrorePubblicazione(api, lang));
    } else {
      void caricaConfigBannerRemota(tid, getApiBase()).then((remota) => {
        avviaBanner(api, unisciConfigBanner(currentScript, remota));
      });
    }
  }

  // Segnale "ready" per chi vuole sincronizzarsi col caricamento
  window.dispatchEvent(new CustomEvent('emerge-privacy-ready'));
})();
